import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

const portraitOne = "https://images.unsplash.com/photo-1517462964-21fdcec3f25b?w=900&q=80";
const portraitTwo = "https://images.unsplash.com/photo-1539125530496-3ca408f9c2d9?w=900&q=80";

export default function Landing() {
  return (
    <div className="min-h-screen grain-bg" data-testid="landing-page">
      <nav className="flex items-center justify-between px-6 sm:px-10 lg:px-16 py-6">
        <div className="font-serif italic text-2xl text-[#913831]" data-testid="brand-logo">
          rendezvous
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/auth?mode=login"
            data-testid="nav-login"
            className="text-sm font-medium text-[#1A1818] hover:text-[#913831] transition-colors"
          >
            Sign in
          </Link>
          <Link
            to="/auth?mode=register"
            data-testid="nav-register"
            className="bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full px-5 py-2 text-sm font-medium transition-transform hover:scale-105"
          >
            Join
          </Link>
        </div>
      </nav>

      <main className="px-6 sm:px-10 lg:px-16 pt-8 pb-24 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="flex items-center gap-2 mb-6 text-xs uppercase tracking-[0.3em] text-[#737373]"
          >
            <span className="w-8 h-px bg-[#913831]" /> Vol. I &middot; Issue 01
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.05 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter text-[#1A1818]"
          >
            Dating, slowed
            <br />
            <em className="text-[#913831]">down</em> on purpose.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-8 text-lg text-[#1A1818]/80 max-w-xl leading-relaxed"
          >
            Rendezvous is a small, editorial dating space for people who&apos;d
            rather write a paragraph than swipe a thousand thumbs. Real
            profiles, gentle pacing, and a Claude-powered editor who actually
            makes you sound like yourself.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/auth?mode=register"
              data-testid="cta-get-started"
              className="inline-flex items-center gap-2 bg-[#913831] hover:bg-[#7A2E28] text-white rounded-full pl-7 pr-5 py-4 font-medium transition-transform hover:scale-[1.02] active:scale-95"
            >
              Begin your story <ArrowRight size={18} strokeWidth={1.75} />
            </Link>
            <Link
              to="/auth?mode=login"
              data-testid="cta-sign-in"
              className="inline-flex items-center gap-2 border border-[#E5DFD3] text-[#1A1818] rounded-full px-6 py-4 font-medium hover:bg-[#F0EBE1] transition-colors"
            >
              I already have an account
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-14 grid grid-cols-3 gap-6 max-w-md"
          >
            {[
              ["Edited", "by Claude"],
              ["Curated", "not gamified"],
              ["Honest", "by design"],
            ].map(([a, b]) => (
              <div key={a}>
                <div className="font-serif text-2xl text-[#913831] italic">{a}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-[#737373] mt-1">{b}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative h-[520px] hidden lg:block">
          <motion.div
            initial={{ opacity: 0, y: 30, rotate: -8 }}
            animate={{ opacity: 1, y: 0, rotate: -6 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="absolute top-0 left-0 w-[280px] bg-white p-3 pb-6 rounded-3xl shadow-[0_30px_60px_-25px_rgba(0,0,0,0.25)] border border-[#E5DFD3]"
          >
            <img src={portraitOne} alt="" className="w-full h-[340px] object-cover rounded-2xl" />
            <div className="px-2 pt-4">
              <div className="font-serif text-2xl text-[#1A1818]">Amelia, 28</div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#737373]">Lisbon</div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 30, rotate: 12 }}
            animate={{ opacity: 1, y: 0, rotate: 8 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="absolute top-16 right-0 w-[280px] bg-white p-3 pb-6 rounded-3xl shadow-[0_30px_60px_-25px_rgba(0,0,0,0.25)] border border-[#E5DFD3]"
          >
            <img src={portraitTwo} alt="" className="w-full h-[340px] object-cover rounded-2xl" />
            <div className="px-2 pt-4">
              <div className="font-serif text-2xl text-[#1A1818]">Jasper, 33</div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#737373]">Porto</div>
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-[#F3E8DD] border border-[#D4A373]/40 text-[#1A1818] rounded-full px-5 py-2 text-sm flex items-center gap-2"
          >
            <Sparkles size={14} className="text-[#913831]" strokeWidth={1.75} />
            <span className="font-medium">Refined with Claude</span>
          </motion.div>
        </div>
      </main>

      <footer className="px-6 sm:px-10 lg:px-16 pb-10 text-xs uppercase tracking-[0.25em] text-[#737373] flex justify-between">
        <span>&copy; rendezvous, an editorial</span>
        <span>love, examined</span>
      </footer>
    </div>
  );
}
